import type { LabelPrintMarginSettings } from "@itaas/label-print";
import { useCallback, useEffect, useMemo, useState } from "react";
import { printProductLabel } from "@/components/supply/ProductLabelPrintSheet";
import type { ProductLabelData } from "@/components/supply/ProductLabelPrintSheet.types";
import {
  getLabelPrintFormat,
  LABEL_PRINT_FORMATS,
  type LabelPrintFormat,
} from "@/core/printing/label-print-formats";
import type { LabelPrintRotation } from "@/core/printing/label-print-rotation";
import {
  fetchPrintBridgePrinters,
  getPrintBridgeBaseUrl,
  invalidatePrintBridgeCache,
  printTestLabelViaBridge,
  probePrintBridge,
  setPrintBridgeDefaultPrinter,
  type PrintBridgePrinter,
} from "@/core/printing/print-bridge-client";
import {
  filterLabelPrinters,
  suggestFormatFromPaperSizes,
  type LabelPrinterProfile,
} from "@itaas/label-print";
import { t } from "@/core/composition/lexicon";

export interface LabelPrintBridgeState {
  connected: boolean;
  selectedPrinter: string;
  driverName?: string;
  printerProfiles?: Record<string, LabelPrinterProfile>;
}

export interface LabelPrintBridgeButtonProps {
  label: ProductLabelData | null;
  format: LabelPrintFormat;
  rotation: LabelPrintRotation;
  margins?: LabelPrintMarginSettings;
  disabled?: boolean;
  onPrinted: () => void;
  onPrintingChange?: (printing: boolean) => void;
  onError?: (message: string | null) => void;
  onSuccess?: (message: string) => void;
  onFormatChange?: (formatId: string) => void;
  onBridgeStateChange?: (state: LabelPrintBridgeState) => void;
  onPrinterChange?: (printerName: string, driverName?: string) => void;
}

export function LabelPrintBridgeButton({
  label,
  format,
  rotation,
  margins,
  disabled = false,
  onPrinted,
  onPrintingChange,
  onError,
  onSuccess,
  onFormatChange,
  onBridgeStateChange,
  onPrinterChange,
}: LabelPrintBridgeButtonProps) {
  const [bridgeChecking, setBridgeChecking] = useState(true);
  const [bridgeConnected, setBridgeConnected] = useState(false);
  const [bridgePrinters, setBridgePrinters] = useState<PrintBridgePrinter[]>([]);
  const [printerProfiles, setPrinterProfiles] = useState<Record<string, LabelPrinterProfile>>({});
  const [selectedPrinter, setSelectedPrinter] = useState("");
  const [showAllPrinters, setShowAllPrinters] = useState(false);
  const [printing, setPrinting] = useState(false);

  const visiblePrinters = useMemo(
    () => filterLabelPrinters(bridgePrinters, showAllPrinters),
    [bridgePrinters, showAllPrinters],
  );

  const selectedPrinterInfo = useMemo(
    () => bridgePrinters.find((p) => p.name === selectedPrinter),
    [bridgePrinters, selectedPrinter],
  );

  const driverFormatSuggestion = useMemo(() => {
    if (!selectedPrinterInfo?.paperSizes?.length) return undefined;
    return suggestFormatFromPaperSizes(LABEL_PRINT_FORMATS, selectedPrinterInfo.paperSizes);
  }, [selectedPrinterInfo]);

  const refreshBridge = useCallback(async () => {
    setBridgeChecking(true);
    invalidatePrintBridgeCache();
    try {
      const health = await probePrintBridge(true);
      if (!health?.ok) {
        setBridgeConnected(false);
        setBridgePrinters([]);
        setSelectedPrinter("");
        return;
      }
      const data = await fetchPrintBridgePrinters(true);
      setBridgeConnected(true);
      setBridgePrinters(data.printers);
      setPrinterProfiles(data.printerProfiles ?? {});
      const preferred =
        data.defaultPrinter ??
        filterLabelPrinters(data.printers, false).find((p) => p.isDefault)?.name ??
        filterLabelPrinters(data.printers, false)[0]?.name ??
        data.printers[0]?.name ??
        "";
      setSelectedPrinter((prev) => {
        const next = prev || preferred;
        if (!prev && next) {
          const info = data.printers.find((p) => p.name === next);
          onPrinterChange?.(next, info?.driverName);
        }
        return next;
      });
    } catch {
      setBridgeConnected(false);
      setBridgePrinters([]);
      setSelectedPrinter("");
    } finally {
      setBridgeChecking(false);
    }
  }, [onPrinterChange]);

  useEffect(() => {
    void refreshBridge();
  }, [refreshBridge]);

  useEffect(() => {
    if (bridgeConnected || bridgeChecking) return undefined;
    const timer = window.setInterval(() => {
      void refreshBridge();
    }, 4000);
    return () => window.clearInterval(timer);
  }, [bridgeConnected, bridgeChecking, refreshBridge]);

  useEffect(() => {
    if (!selectedPrinter && visiblePrinters.length > 0) {
      setSelectedPrinter(visiblePrinters[0].name);
    }
  }, [selectedPrinter, visiblePrinters]);

  const setPrintingState = (next: boolean) => {
    setPrinting(next);
    onPrintingChange?.(next);
  };

  useEffect(() => {
    onBridgeStateChange?.({
      connected: bridgeConnected,
      selectedPrinter,
      driverName: selectedPrinterInfo?.driverName,
      printerProfiles,
    });
  }, [bridgeConnected, selectedPrinter, selectedPrinterInfo?.driverName, printerProfiles, onBridgeStateChange]);

  const handlePrinterChange = (printerName: string) => {
    setSelectedPrinter(printerName);
    const info = bridgePrinters.find((p) => p.name === printerName);
    onPrinterChange?.(printerName, info?.driverName);
    void setPrintBridgeDefaultPrinter(printerName).catch(() => undefined);
  };

  const handlePrint = async () => {
    if (!label || disabled || printing || !bridgeConnected) return;
    setPrintingState(true);
    onError?.(null);
    try {
      await printProductLabel(label, {
        format,
        rotation,
        margins,
        mode: "bridge",
        printerName: selectedPrinter || undefined,
        onPrinted: () => {
          onPrinted();
          setPrintingState(false);
        },
      });
    } catch (err) {
      setPrintingState(false);
      onError?.(err instanceof Error ? err.message : "Impression échouée.");
    }
  };

  const handleTestPrint = async () => {
    if (printing || !bridgeConnected) return;
    setPrintingState(true);
    onError?.(null);
    try {
      const result = await printTestLabelViaBridge(
        format,
        rotation,
        selectedPrinter || undefined,
        margins,
      );
      setPrintingState(false);
      onSuccess?.(`Étiquette test envoyée à ${result.printerName}.`);
    } catch (err) {
      setPrintingState(false);
      onError?.(err instanceof Error ? err.message : "Étiquette test échouée.");
    }
  };

  const handleApplyDriverFormat = () => {
    if (!driverFormatSuggestion || !onFormatChange) return;
    onFormatChange(driverFormatSuggestion.format.id);
  };

  const canPrint = Boolean(label) && !disabled && bridgeConnected && !bridgeChecking;

  return (
    <div className="d-flex flex-column gap-2">
      {bridgeChecking && (
        <p className="small text-muted mb-0">Connexion à ITaaS Print Bridge…</p>
      )}

      {!bridgeChecking && bridgeConnected && (
        <>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <span className="badge text-bg-success">Print Bridge</span>
            <span className="small text-muted">{getPrintBridgeBaseUrl()}</span>
          </div>
          {visiblePrinters.length > 0 && (
            <div>
              <div className="d-flex align-items-center justify-content-between gap-2 mb-1">
                <label className="form-label small fw-semibold mb-0" htmlFor="label-bridge-printer">
                  Imprimante thermique
                </label>
                <button
                  type="button"
                  className="btn btn-link btn-sm p-0"
                  onClick={() => setShowAllPrinters((v) => !v)}
                >
                  {showAllPrinters ? "Masquer virtuelles" : "Toutes les imprimantes"}
                </button>
              </div>
              <select
                id="label-bridge-printer"
                className="form-select form-select-sm"
                value={selectedPrinter}
                onChange={(e) => handlePrinterChange(e.target.value)}
                disabled={printing}
              >
                {visiblePrinters.map((printer) => (
                  <option key={printer.name} value={printer.name}>
                    {printer.name}
                    {printer.isDefault ? " (défaut Windows)" : ""}
                  </option>
                ))}
              </select>
            </div>
          )}
          {driverFormatSuggestion && onFormatChange && driverFormatSuggestion.format.id !== format.id && (
            <div className="alert alert-info py-2 mb-0 small">
              Format driver détecté : <strong>{driverFormatSuggestion.paperName}</strong> (
              {driverFormatSuggestion.format.widthMm}×{driverFormatSuggestion.format.heightMm} mm).
              <button
                type="button"
                className="btn btn-link btn-sm p-0 ms-1 align-baseline"
                onClick={handleApplyDriverFormat}
              >
                Appliquer « {getLabelPrintFormat(driverFormatSuggestion.format.id)?.label ?? driverFormatSuggestion.format.id} »
              </button>
            </div>
          )}
          {selectedPrinterInfo?.paperSizes && selectedPrinterInfo.paperSizes.length > 0 && (
            <p className="small text-muted mb-0">
              Formats papier driver :{" "}
              {selectedPrinterInfo.paperSizes
                .slice(0, 4)
                .map((p) => `${p.name} (${p.widthMm}×${p.heightMm} mm)`)
                .join(" · ")}
              {selectedPrinterInfo.paperSizes.length > 4 ? " …" : ""}
            </p>
          )}
        </>
      )}

      {!bridgeChecking && !bridgeConnected && (
        <div className="alert alert-warning py-2 mb-0 small">
          <strong>Print Bridge indisponible.</strong> Lancez{" "}
          <code>pnpm dev:print-bridge</code> ou{" "}
          <code>.\scripts\start-hospitol.ps1</code>.
          <button
            type="button"
            className="btn btn-link btn-sm p-0 ms-1 align-baseline"
            onClick={() => void refreshBridge()}
          >
            Réessayer
          </button>
        </div>
      )}

      <div className="d-flex flex-wrap gap-2">
        <button
          type="button"
          className="btn btn-primary btn-sm"
          disabled={!canPrint || printing}
          onClick={() => void handlePrint()}
          title="Impression TSPL directe (sans Chrome)"
        >
          {printing ? "Impression…" : t("action.printLabel")}
        </button>
        {bridgeConnected && (
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            disabled={printing || bridgeChecking}
            onClick={() => void handleTestPrint()}
            title="Étiquette test sans article"
          >
            Étiquette test
          </button>
        )}
      </div>
    </div>
  );
}
